import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight, Replace } from 'lucide-react';
import {
  ONU_SWAP_MATCH_LABELS,
  ONU_SWAP_TRIGGER_LABELS,
  type OnuSwap,
} from '@simpleacs/shared';
import { api } from '../../lib/api';
import { onuPath } from '../../lib/links';
import { formatRelative, formatTimestamp } from '../../lib/format';
import { Card, EmptyState, ErrorNotice, Pill, Spinner } from '../../components/ui';

/**
 * The hardware that came before this subscriber's ONU — and the ONLY place in
 * the platform where a previous unit's identity is shown.
 *
 * A swap is not a migration. A migration moves ONE unit to another slot and the
 * subscriber's number goes with it; this is TWO units on one service, because
 * the box died and an engineer hung a different one on the drop. Every row here
 * is a permanent fact about a subscriber: a serial that used to be theirs, when
 * it stopped being, and what the platform's evidence for saying so was.
 *
 * Read from the swap record rather than derived from the ONU table, which
 * matters most on the two paths that destroy the old row: deauthorising deletes
 * it, and authorising a replacement onto the same slot reuses it. On both, this
 * tab is the last thing that still knows what was there.
 */
export function SwapsTab({ onuKey, serial }: { onuKey: string; serial: string }) {
  const query = useQuery({
    queryKey: ['onu', onuKey, 'swaps'],
    queryFn: () => api.get<{ items: OnuSwap[] }>(`/onus/${encodeURIComponent(onuKey)}/swaps`),
  });

  const items = query.data?.items ?? [];
  const here = serial.toUpperCase();

  return (
    <div className="space-y-5">
      <Card
        title={
          <span className="flex items-center gap-2">
            <Replace className="size-4 text-muted" />
            Hardware history
            <span className="text-xs font-normal text-faint">{items.length}</span>
          </span>
        }
      >
        {query.isError ? (
          <div className="p-5">
            <ErrorNotice error={query.error} />
          </div>
        ) : query.isLoading ? (
          <div className="flex justify-center p-8">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          /* An honest empty state, not a spinner and not an apology. Most ONUs
             on a fleet have never been replaced, and saying so is the answer to
             the question the operator opened this tab with. */
          <EmptyState
            title="No ONU has been replaced on this service"
            description={`${serial} is the only unit SimpleACS has recorded here. A replacement is written down when an OLT sync sees one arrive, and when an operator authorises one over the top of another.`}
          />
        ) : (
          <ul className="divide-y divide-line">
            {items.map((swap) => (
              <SwapRow key={swap.id} swap={swap} here={here} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/**
 * One replacement, read left to right: what left, what took its place.
 *
 * Both units are named the way a person says them — the serial, the model and
 * the subscriber the chassis carries — and linked by their platform NUMBER
 * where the row still exists. A row id never appears: the old unit is either a
 * page an operator can open or a serial they can read out, and there is no
 * third thing worth showing them.
 */
function SwapRow({ swap, here }: { swap: OnuSwap; here: string }) {
  /*
    Each side is judged against THIS page's serial on its own.

    `!previousIsThis` was the bug: on a slot that has held three units — which
    `swapHistoryFor` deliberately returns as a chain — the middle row has this
    ONU on neither side, and the replacement was then badged "this ONU" and lost
    its link. Concretely on this fleet: 1/2/6:2 goes HWTC0F5D180C to
    ZXICCD580650 in the backfill, the ZTE later dies and a third serial is
    authorised onto the slot. Opening the third unit shows two rows, and the
    older one called the ZTE "this ONU" while the reader was looking at the F670
    that replaced it.
  */
  const previousIsThis = swap.previous.serial.toUpperCase() === here;
  const replacementIsThis = swap.replacement.serial.toUpperCase() === here;
  const slotMoved =
    swap.previous.ponPort !== swap.replacement.ponPort ||
    swap.previous.onuIndex !== swap.replacement.onuIndex;

  return (
    <li className="px-5 py-4">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
        <span className="font-mono">{formatTimestamp(swap.detectedAt)}</span>
        <span className="text-faint">·</span>
        <span>{formatRelative(swap.detectedAt)}</span>
        <span className="text-faint">·</span>
        <span>{ONU_SWAP_TRIGGER_LABELS[swap.trigger]}</span>
        {swap.actor && (
          <>
            <span className="text-faint">·</span>
            <span>{swap.actor}</span>
          </>
        )}
        {/* How the two units were tied together, always stated. `slot` is the
            new one taking the old one's exact place, which is physical; the
            other is an inference from the subscriber on the port, and an
            operator reading history is entitled to know which they have. */}
        <Pill state tone={swap.matchedBy === 'slot' ? 'neutral' : 'warn'}>
          {ONU_SWAP_MATCH_LABELS[swap.matchedBy]}
        </Pill>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
        <Unit
          heading="Replaced"
          serial={swap.previous.serial}
          model={swap.previous.model}
          name={swap.previous.name}
          ponPort={swap.previous.ponPort}
          onuIndex={swap.previous.onuIndex}
          number={swap.previous.number}
          onuId={swap.previous.onuId}
          isThis={previousIsThis}
          footnote={
            swap.previous.lastSeenAt
              ? `Last up ${formatTimestamp(swap.previous.lastSeenAt)}`
              : swap.trigger === 'operator'
                ? 'Retired by Swap — its serial and model are all that is kept'
                : 'Never seen working by SimpleACS'
          }
        />
        <ArrowRight className="hidden size-4 shrink-0 text-faint sm:block" />
        <Unit
          heading="Replaced by"
          serial={swap.replacement.serial}
          model={swap.replacement.model}
          name={swap.replacement.name}
          ponPort={swap.replacement.ponPort}
          onuIndex={swap.replacement.onuIndex}
          number={swap.replacement.number}
          onuId={swap.replacement.onuId}
          isThis={replacementIsThis}
          footnote={swap.oltName ? `on ${swap.oltName}` : null}
        />
      </div>

      {/* Said out loud, because it is the thing an operator would otherwise have
          to notice by comparing two slot strings — and it is the whole reason
          the slot rule alone would have missed this subscriber. */}
      {slotMoved && (
        <p className="mt-3 text-xs text-muted">
          The replacement was authorised at {swap.replacement.ponPort}:{swap.replacement.onuIndex},
          not at {swap.previous.ponPort}:{swap.previous.onuIndex} where the old unit was — so the
          old declaration may still be on the chassis.
        </p>
      )}

      {/* The ACS's own name for the retired unit. It is here and nowhere else on
          purpose: it is not reliably the PON serial (an F670L informs a router
          serial), so it is the only safe handle on the GenieACS row the unit
          left behind. */}
      {swap.previous.deviceSerial && (
        <p className="mt-1 text-xs text-muted">
          The ACS knew the retired unit as{' '}
          <span className="font-mono text-ink">{swap.previous.deviceSerial}</span>.
        </p>
      )}
    </li>
  );
}

function Unit({
  heading,
  serial,
  model,
  name,
  ponPort,
  onuIndex,
  number,
  onuId,
  isThis,
  footnote,
}: {
  heading: string;
  serial: string;
  model: string | null;
  name: string | null;
  ponPort: string;
  onuIndex: string;
  number: number | null;
  onuId: string | null;
  isThis: boolean;
  footnote: string | null;
}) {
  const label = (
    <span className="font-mono text-sm text-ink">{serial}</span>
  );
  return (
    <div className="min-w-0">
      <div className="text-xs uppercase tracking-wide text-faint">{heading}</div>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {/* Linked only where the ONU still has a page to open. A deauthorise
            deletes the row and an authorise reuses it, and in both cases the
            serial recorded here is all that is left of that unit — a link built
            from a number that now belongs to somebody else would be worse than
            no link at all. */}
        {number !== null && onuId !== null && !isThis ? (
          <Link to={onuPath({ number, id: onuId })} className="hover:underline">
            {label}
          </Link>
        ) : (
          label
        )}
        {isThis && <Pill tone="success">this ONU</Pill>}
      </div>
      <div className="mt-0.5 truncate text-xs text-muted">
        {[model, name].filter(Boolean).join(' · ') || 'Model not reported'}
      </div>
      <div className="mt-0.5 font-mono text-xs text-faint">
        {ponPort}:{onuIndex}
      </div>
      {footnote && <div className="mt-0.5 text-xs text-faint">{footnote}</div>}
    </div>
  );
}
